import type {
  SymbolLayerSpecification,
  LineLayerSpecification,
  FillLayerSpecification,
} from 'mapbox-gl'

// Style-layer id suffixes createMapboxLayer produces, in the z-order the
// layers are added (bottom first). Consumers derive removal lists and
// z-anchoring from this — never hardcode the suffixes elsewhere.
export const GEOJSON_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP = [
  '-polygon-fill',
  '-polygon-outline',
  '-line',
  '-line-label',
  '-point',
]

export const createMapboxLayer = ({ id }: { id: string }) => {
  const pointLayer: SymbolLayerSpecification = {
    id: `${id}-point`,
    type: 'symbol',
    source: id,
    layout: {
      'icon-image': 'dot-11',
      'text-field': ['get', 'name'],
      'text-size': 14,
      'text-anchor': 'center',
      'text-offset': [0, -1],
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
    },
    filter: ['==', '$type', 'Point'],
  }

  const lineLayer: LineLayerSpecification = {
    id: `${id}-line`,
    type: 'line',
    source: id,
    paint: {
      'line-color': ['coalesce', ['to-color', ['get', 'stroke']], '#000000'],
      'line-width': ['coalesce', ['get', 'stroke-width'], 1],
    },
    filter: ['==', '$type', 'LineString'],
  }
  const lineLabelLayer: SymbolLayerSpecification = {
    id: `${id}-line-label`,
    type: 'symbol',
    source: id,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': 12,
      'symbol-placement': 'line', // Ensures the text follows the line geometry
      'text-anchor': 'center',
    },
    paint: {
      'text-color': '#000000',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1,
    },
    filter: ['==', '$type', 'LineString'], // Apply to lines only
  }

  const polygonLayerFill: FillLayerSpecification = {
    id: `${id}-polygon-fill`,
    type: 'fill',
    source: id,
    paint: {
      'fill-color': ['coalesce', ['to-color', ['get', 'fill']], '#ff4242'],
      'fill-outline-color': ['to-color', ['get', 'stroke'], '#ff4242'],
      'fill-antialias': false,
      'fill-opacity': 0.3,
    },
    filter: ['==', '$type', 'Polygon'],
  }

  const polygonLayerOutline: LineLayerSpecification = {
    id: `${id}-polygon-outline`,
    type: 'line',
    source: id,
    paint: {
      'line-color': ['coalesce', ['to-color', ['get', 'stroke']], '#ff4242'],
      'line-width': ['coalesce', ['get', 'stroke-width'], 1],
    },
    filter: ['==', '$type', 'Polygon'],
  }

  // The order of the layers is important to ensure that points appear on top of lines and polygons
  return [
    polygonLayerFill,
    polygonLayerOutline,
    lineLayer,
    lineLabelLayer,
    pointLayer,
  ]
}
